import React, {
    FC, createContext, useContext, useState, ReactNode, MouseEvent, useEffect, useRef
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import {
    PageHeader, Space, Table, Form, Select, Checkbox, Button, Typography, Popover, ConfigProvider
} from 'antd'
import {
    DownOutlined, SearchOutlined, CloudDownloadOutlined,
    FileSearchOutlined, ClockCircleOutlined, NodeIndexOutlined
} from '@ant-design/icons'
import type { TableProps, PaginationProps } from 'antd'
import zhCN from 'antd/es/locale/zh_CN'
import { useTranslation } from 'react-i18next'
import type { FormInstance } from 'antd/es/form'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import type { FilterValue } from 'antd/es/table/interface'
import type { IPagination, IProjectInfo, IProjectInfoResData } from '@/store/models/tableEdit.model'
import { getWorkgroup } from '@/store/actions/tableEdit.action'
import { getProjectInfoList } from '@/services/table.service'
import { IAppState } from '@/store/reducers'
import Table2SearchPop from '@/pages/table2Mana/components/table2GlobalSearchPop'
import LogListPop from '@/pages/table2Mana/components/logListPop'
import HistoryListPop from '@/pages/table2Mana/components/historyListPop'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import { getDicClass } from '@/pages/charts'
import { getUuid, downloadFile, autoClick } from '@/utils/util'
import { exportExcel } from '@/services/table.service'
import { getErrorStr } from '@/store/sagas/table.saga'
import { requestFail } from '@/store/actions/common.action'
import { getBUList, getProdList } from '@/services/progress.service'
import Nav from '@/components/nav'
import ResizableTable from '@/components/resizableTable'
interface EditableRowProps {
    index: number
}
interface EditableCellProps {
    title: ReactNode
    editable: boolean
    children: ReactNode
    dataIndex: string
    record: any
}
interface childrenType {
    title: string
    dataIndex: string
    key: string
    editable?: boolean
    className?: string
    width?: number
    ellipsis: { showTitle: boolean },
    filters?: {
        text: string
        value: number
    }[]
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    onFilter?: (value: string, record: any) => boolean
    filteredValue?: FilterValue | null
    render?: any
}
export interface IColProperty {
    title?: ReactNode | string
    className?: string
    editable?: boolean
    dataIndex?: string | string[]
    children?: childrenType[]
    filters?: {
        text: string
        value: string
    }[]
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    onFilter?: (value: string | number | boolean, record: any) => boolean
}
type EditableTableProps = Parameters<typeof Table>[0]
type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

export const wrapText = (text: string): ReactNode => {
    const br = <br/>
    const empty = <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
    let result
    const contentStr = text ? text.split(';') : ''
    if(contentStr.length < 2) {
        return text
    }
    for(let j = 0; j < contentStr.length; j++) {
        if (j == 0) {
            result = contentStr[j]
        } else if (contentStr[j].startsWith(' ')) {
            result = <span>{result}{br}{empty}{contentStr[j]}</span>
        } else {
            result = <span>{result}{br}{contentStr[j]}</span>
        }
    }
    return <div>{result}</div>
}

const EditableContext = createContext<FormInstance<any> | null>(null)
const DataSourceContext = createContext<{
    dataSource: IProjectInfo[]
    setDataSource: any
} | null>(null)
export const DrawerShowContext = createContext<{
    showSearchPop: boolean
    setShowSearchPop: any
    setSearchObj: any
    classArr: any[]
    prodList: any[]
    buList: any[]
}>({
    showSearchPop: false,
    setShowSearchPop: null,
    setSearchObj: null,
    classArr: [],
    prodList: [],
    buList: [],
})

const EditableRow: FC<EditableRowProps> = ({ ...props }) => {
    const [form] = Form.useForm()
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    )
}
const EditableCell: FC<EditableCellProps> = ({
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    title,
    editable,
    children,
    dataIndex,
    record,
    ...restProps
}) => {
    const { Option } = Select
    const [editing, setEditing] = useState(false)
    const form = useContext(EditableContext)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { dataSource, setDataSource } = useContext(DataSourceContext)!
  
    const toggleEdit = (value?: string | MouseEvent<HTMLDivElement>): void => {
        if (!record) return
        setEditing(!editing)
        // 修改 form 值为 value
        form?.setFieldsValue({ [dataIndex]: value || record[dataIndex] })
    }

    const saveChange = async (e: string) => {
        const newData = [...dataSource]
        const findIndex = newData.findIndex(item => ('key' in item ? item.key === record.key : item.id === record.id))
        if (findIndex !== -1) {
            const item = newData[findIndex]
            // 修改原 dataSource
            newData.splice(findIndex, 1, {
                ...item,
                [dataIndex]: e ? e.toUpperCase() : ''
            })
            setDataSource(newData)
        }
        try {
            toggleEdit(e)
        } catch (errInfo: any) {
            console.log('Save failed:', errInfo.message)
        }
    }
    const save = () => {
        try {
            toggleEdit()
        } catch (errInfo: any) {
            console.log('Save failed:', errInfo.message)
        }
    }
    const saveStop = (e: MouseEvent<HTMLDivElement>): void => {
        e.stopPropagation()
    }
  
    let childNode = children
  
    if (editable) {
        const bg = record[dataIndex] === 'ON' ? 'on_bg' : (record[dataIndex] === 'PLAN' ? 'plan_bg' : 'empty_bg')
        childNode = editing ? (
            <Form.Item className={`table_input ${bg}`}
                style={{ margin: 0 }}
                name={dataIndex}
                initialValue={{ [dataIndex]: 'on' }}
            >
                <Select style={{ width: '100%' }} 
                    onChange={saveChange}
                    onBlur={save}
                    onClick={saveStop}
                    allowClear
                >
                    <Option value="on">ON</Option>
                    <Option value="plan">PLAN</Option>
                </Select>
            </Form.Item>
        ) : (
            <div className={`editable-cell-value-wrap ${bg}`}
                style={{ paddingRight: 24 }}
                onClick={toggleEdit}
            >
                { children }
            </div>
        )
    }
    
    return <td {...restProps} onClick={save}>{ childNode }</td>
}

const TablePage2: FC = () => {
    const dispatch = useDispatch()
    const history: any = useHistory()
    const locationObj = useLocation<any>()
    const { t, i18n } = useTranslation()
    const route = useSelector((state: IAppState) => state.router)
    const authInfo = useSelector((state: IAppState) => state.auth.authInfo)
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)
    const classification = useSelector((state: IAppState) => state.tableEditData.techClassification)
    const dicClassList = useSelector((state: IAppState) => state.tableEditData.dicClass)
    
    const { Text } = Typography
    const { Option } = Select
    
    const [init, setInit] = useState(true)
    const [classArr, setClassArr] = useState<any[]>([])
    const [dataSource, setDataSource] = useState<IProjectInfo[]>([])
    const [projectInfo, setProjectInfo] = useState<IProjectInfo[]>([])
    const [winH, setWinh] = useState<number>(0)
    const [columns, setColumns] = useState<any[]>([])
    // 是否已获取过 dicClassList
    const [allCheck, setAllCheck] = useState<boolean>(true)
    const [prodList, setProdList] = useState<any[]>([])
    const [buList, setBuList] = useState<any[]>([])
    // 所有列
    const [dataIndexs, setDataIndexs] = useState<string[]>([])
    // 选择可显示列下拉列表
    const [filterOptions, setFilterOptions] = useState<any[]>([])
    // 保存选择的列
    const [filterArr, setFilterArr] = useState<string[]>([])
    // 列内容搜索
    const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({})
    const [paginationInfo, setPaginationInfo] = useState<IPagination>({
        pageIndex: 1,
        pageSize: 10,
        total: 0
    })
    const [tableLoading, setTableLoading] = useState(true)
    const [exportLoading, setExportLoading] = useState(false)

    const allFilter = useRef<any[]>([])
    // pageSize 变更后，会重复触发 pageChange 事件
    const pageSizeChanged = useRef<boolean>(false)
    const didMountRef = useRef(false)

    // 高级搜索
    const [showSearchPop, setShowSearchPop] = useState(false)
    const [searchObj, setSearchObj] = useState<any>({})
    const [tableKey, setTableKey] = useState('0')
    // 表格搜索条件, 列标题上的搜索
    // const searchColumnCont = useRef<{
    //     value: string | number | boolean
    //     fieldname: string
    //     record: any
    // }>({
    //     value: '',
    //     fieldname: '',
    //     record: null
    // })
    // 表格渲染当前分类 id , 同一类只有第一条设置 rowspan , 后面的全部设为 0 
    // const rowClassObj = useRef<{
    //     [id: string]: number
    // } | null>(null)
    const [viewLogId, setViewLogId] = useState<number | null>(null)
    const [viewHistoryId, setViewHistoryId] = useState<number | null>(null)

    const viewButton = (text: string, record: any) => {
        return authInfo ? (
            <Space>
                {Reflect.has(authInfo, 'viewDetailProjectInfo') && <a target='_blank' rel='noopener'
                    href={`/projectInfoView?workGroupId=${record.techClass.workGroup.id}&projectId=${record.id}&isView=true`}
                ><Text className='txt_blue hand' title={t('view')}><FileSearchOutlined style={{ fontSize: 16 }} /></Text></a>}
                {Reflect.has(authInfo, 'viewLogProjectInfo') && <Text className='txt_blue hand' title={t('viewLog')}
                    onClick={() => {
                        setViewLogId(record.id)
                    }}
                ><ClockCircleOutlined style={{ fontSize: 16 }} /></Text>}
                {Reflect.has(authInfo, 'viewHistoryProjectInfo') && <Text className='txt_blue hand' title={t('viewHistory')}
                    onClick={() => {
                        setViewHistoryId(record.id)
                    }}
                ><NodeIndexOutlined style={{ fontSize: 16 }} /></Text>}
            </Space>
        ) : <></>
    }

    // 添加年的列
    const getYearColumns = (minYear: number, maxYear: number) => {
        const years = []
        const _dataIndexs = []
        for (let year = minYear; year <= maxYear; year++) {
            const _year = String(year)
            const _obj: IColProperty = {
                title: _year + (i18n.language !== 'zh' ? ' ' : '') + t('year'),
                className: 'year_col',
                children: []
            }
            const children: childrenType[] = []
            for (let i = 0; i < 4; i++) {
                const dataIndex = `${_year.substring(2)}-Q${i + 1}`
                _dataIndexs.push(dataIndex)
                children.push({
                    title: `Q${i + 1}`,
                    dataIndex,
                    key: `${_year.substring(2)}-Q${i + 1}`,
                    className: 'year_col',
                    ellipsis: { showTitle: true },
                    width: 100,
                    editable: false,
                    render: (text: number) => {
                        // return <div className='edit_txt'>{text || ''}</div>
                        return <div className={text ? (text === 1 ? 'plan_bg' : 'on_bg') : 'empty_bg'}>
                            {text ? (text === 1 ? 'PLAN' : 'ON') : ''}
                        </div>
                    },
                })
            }
            _obj.children = children
            years.push(_obj)
        }
        return { 
            years,
            yearDataIndexs: _dataIndexs 
        }
    }
    
    // 分页、排序、筛选变化时触发
    const handleChange: TableProps<any>['onChange'] = (pagination, filters) => {
        setFilteredInfo(filters)
    }
    const handleRow: TableProps<any>['onRow'] = (record: IProjectInfo) => {
        return {
            onDoubleClick: event => {
                console.log('handleRow: ', record, event)
                if (authInfo && Reflect.has(authInfo, 'viewDetailProjectInfo')) {
                    autoClick(`/projectInfoView?workGroupId=${record.techClass.workGroup.id}&projectId=${record.id}&isView=true`)
                }
            },
        }
    }
    const searchFn = async () => {
        try {
            console.log('searchFun: ', searchObj)
            getList({
                ...paginationInfo,
                pageIndex: 1,
            })
            setPaginationInfo({
                ...paginationInfo,
                pageIndex: 1,
            })
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }
    const getList = async (params?: IPagination) => {
        try {
            setTableLoading(true)
            const res = await getProjectInfoList({
                ...searchObj,
                pageNo: params?.pageIndex || paginationInfo.pageIndex,
                pageSize: params?.pageSize || paginationInfo.pageSize,
            }).finally(() => {
                setTableLoading(false)
            })
            console.log('getProjectInfoList: ', res)
            const content = res.content as IProjectInfoResData[]
            const projectInfoList = content.map(item => {
                const newItem: IProjectInfo = {
                    ...item,
                    techClassId: item.techClass.id,
                    classificationName: item.techClass.classificationName
                }
                item.techYearQuarters.forEach(yearItem => {
                    newItem[`${String(yearItem.year).substring(2)}-Q${yearItem.quarter}id`] = yearItem.id
                    newItem[`${String(yearItem.year).substring(2)}-Q${yearItem.quarter}`] = yearItem.status
                })
                return newItem
            })
            setProjectInfo([...projectInfoList])
            setDataSource([...projectInfoList])
            setPaginationInfo({
                pageIndex: params?.pageIndex || paginationInfo.pageIndex,
                pageSize: params?.pageSize || paginationInfo.pageSize,
                total: res.totalElements
            })
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }

    // 获取 product list
    const getProductList = async () => {
        const res = await getProdList({
            pageNo: 1,
            pageSize: 10000
        })
        setProdList(res.content)
    }
    // // 获取 bu list
    const getBuListFun = async () => {
        const res = await getBUList()
        setBuList(res)
    }

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    }
    const routes = [
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/projectInfo',
            breadcrumbName: t('Globle Search'),
        },
    ]
    const upperCaseFirst = (str: string | string[]) => {
        const letterArr = typeof str === 'string' ? str.split('') : str[str.length - 1].split('')
        if (letterArr.indexOf('-') !== -1) {
            return letterArr.join('')
        }
        const letters = letterArr.join('')
        switch (letters) {
            case 'operation':
                return t('Action')
            case 'classificationName':
                return t('techClass')
            case 'techPtn':
                return t('PTN')
            case 'techName':
                return t('Tech Name')
            case 'tmtapproval':
                return t('tmtApproval')
            case 'deliveryReview':
                return t('techDeliveryReview')
            case 'platforms':
                return t('productPlatform')
            case 'relevanceBuss':
                return t('buStakeholder')
            case 'dutyBuss':
                return t('developDutySubject')
            case 'techMaturity':
                return t('maturityStar')
        }
        return t(letters)
    }
    // 筛选列下拉选项
    const getFilterOptions = () => dataIndexs.map((dataIndex: string, index) => (
        <Option key={dataIndex} value={dataIndex} title={upperCaseFirst(dataIndex)}>
            <Checkbox checked={filterArr.includes(dataIndex)}
                disabled={index < 6 || index === allFilter.current.length - 1}
                className='select_col_option'
                onChange={(e: CheckboxChangeEvent) => {
                    const _arr: string[] = [...filterArr]
                    if (e.target.checked) {
                        _arr.push(dataIndex)
                    } else {
                        const findIndex = _arr.findIndex(item => item === dataIndex)
                        _arr.splice(findIndex, 1)
                    }
                    setAllCheck(_arr.length === allFilter.current.length)
                    setFilterArr(_arr)
                }}
            >{upperCaseFirst(dataIndex)}</Checkbox>
        </Option>
    ))

    const defaultColumns: (ColumnTypes[number] & IColProperty)[] = [
        {
            title: () => <span>{t('Work Group')}</span>,
            dataIndex: 'techClass',
            key: 'techClass',
            width: 150,
            ellipsis: { showTitle: true },
            fixed: true,
            render (value) {
                return value.workGroup.workGroupName
            }
        },
        {
            title: () => <span>{t('Classification')}</span>,
            dataIndex: 'classificationName',
            key: 'classification',
            width: 150,
            ellipsis: { showTitle: true },
            fixed: true,
        },
        {
            title: () => <span>{t('PTN')}</span>,
            dataIndex: 'techPtn',
            key: 'techPtn',
            width: 150,
            className: 'lang_column',
            ellipsis: { showTitle: true },
            fixed: true,
            // filterIcon: (filtered: boolean) => (
            //     <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            // ),
            // onFilter: (value, record) => {
            //     const result = record['techPtn']
            //         .toString()
            //         .toLowerCase()
            //         .includes((value as string).toLowerCase())
            //     return result
            // },
            render: wrapText
        },
        {
            title: () => <span>{t('researchForms')}</span>,
            dataIndex: 'researchForms',
            key: 'researchForms',
            width: 150,
            className: 'search_col lang_column',
            ellipsis: { showTitle: true },
            // filters: dicClassList['research form']
            //     ? dicClassList['research form'].map(item =>
            //         ({ text: item.name, value: item.value }))
            //     : [],
            // onFilter: (value, record) => {
            //     searchColumnCont.current = {
            //         value,
            //         fieldname: 'researchForms',
            //         record
            //     }
            //     // 重新设置 rowSpan
            //     rowClassObj.current = null
            //     const filedValue = record['researchForms']
            //     const result = filedValue ? filedValue.includes(value) : false
            //     return result
            // }
        },
        {
            title: () => <span>{t('Technology Name')}</span>,
            dataIndex: 'techName',
            key: 'technologyName',
            width: 180,
            className: 'lang_column',
            ellipsis: { showTitle: true },
            // render: wrapText
            render (value) {
                if (value && (value.includes('<div') || value.includes('<span'))) {
                    return <Popover 
                        title=""
                        content={
                            <div dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}></div>
                        }
                    >
                        <div style={{ maxWidth: 180 }}
                            className='table_ellipsis' dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}
                        ></div>
                    </Popover>
                } else {
                    return <div style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={value}
                    >
                        {wrapText(value)}
                    </div>
                }
            }
        },
        {
            title: () => <span>{t('subsidiary')}</span>,
            dataIndex: 'subsidiary',
            key: 'subsidiary',
            width: 150,
            className: 'lang_column',
            ellipsis: { showTitle: true },
            render (value) {
                if (value && (value.includes('<div') || value.includes('<span'))) {
                    return <div
                        dangerouslySetInnerHTML={{
                            __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                        }}
                    ></div>
                } else {
                    return <div style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={value}
                    >
                        {wrapText(value)}
                    </div>
                }
            }
        },
        {
            title: () => <span>{t('description')}</span>,
            dataIndex: 'description',
            width: 280,
            ellipsis: { showTitle: true },
            key: 'description',
            render (value) {
                if (value && (value.includes('<div') || value.includes('<span'))) {
                    return <Popover 
                        placement="topLeft"
                        title=""
                        content={
                            <div style={{ maxWidth: 240, maxHeight: 300, overflowY: 'auto', wordBreak: 'break-all' }}
                                dangerouslySetInnerHTML={{
                                    __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                                }}
                            ></div>
                        }
                    >
                        <div style={{ maxWidth: 240 }}
                            className='table_ellipsis'
                            dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}
                        ></div>
                    </Popover>
                } else {
                    return <div style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={value}
                    >
                        {wrapText(value)}
                    </div>
                }
            }
        },
        {
            title: () => <span>{t('techLevel')}</span>,
            dataIndex: 'techLevel',
            key: 'techLevel',
            width: 100,
            className: 'search_col lang_column',
            ellipsis: { showTitle: true },
            render (value) {
                return [t('core'), t('key'), t('general'), t('normal')][value - 1]
            }
            // filters: [
            //     { text: 'YES', value: 'YES' },
            //     { text: 'No', value: 'No' },
            // ],
            // onFilter: (value, record) => {
            //     const filedValue = record['keyTech']
            //     const result = filedValue ? filedValue.includes(value) : false
            //     return result
            // }
        },
        {
            title: () => <span>{t('priority')}</span>,
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
            className: 'lang_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('quantitativeIndicators')}</span>,
            dataIndex: 'quantitativeIndicators',
            key: 'quantitativeIndicators',
            width: 280,
            ellipsis: { showTitle: true },
            render (value) {
                if (value && (value.includes('<div') || value.includes('<span'))) {
                    return <Popover 
                        placement="topLeft"
                        title=""
                        content={
                            <div dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}></div>
                        }
                    >
                        <div style={{ maxWidth: 240 }}
                            className='table_ellipsis' 
                            dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}
                        ></div>
                    </Popover>
                } else {
                    return <div style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={value}
                    >
                        {wrapText(value)}
                    </div>
                }
            }
        },
        {
            title: () => <span>{t('tmtApproval')}</span>,
            dataIndex: 'tmtapproval',
            key: 'TMTApproval',
            width: 100,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('almCode')}</span>,
            dataIndex: 'almCode',
            key: 'almCode',
            width: 100,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('techDeliveryReview')}</span>,
            dataIndex: 'deliveryReview',
            key: 'deliveryReview',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('maturity')}</span>,
            dataIndex: 'maturity',
            key: 'maturity',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('maturityStar')}</span>,
            dataIndex: 'techMaturity',
            key: 'maturityDefine',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: string) {
                if (dicClassList['技术成熟度']) {
                    const findObj = dicClassList['技术成熟度'].find(item => item.value === val)
                    if (findObj) {
                        return findObj.name
                    }
                }
                return ''
            }
        },
        {
            title: () => <span>{t('isMajorProject')}</span>,
            dataIndex: 'isMajorProject',
            key: 'isMajorProject',
            width: 100,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: boolean) {
                return val ? (<span>{t('Yes')}</span>) : (<span>{t('No')}</span>)
            }
        },
        {
            title: () => <span>{t('requirementSource')}</span>,
            dataIndex: 'requirementSource',
            key: 'requirementSource',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('developedBy')}</span>,
            dataIndex: 'developedBy',
            key: 'developedBy',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('product') + (i18n.language !== 'zh' ? ' ' : '') + t('platform')}</span>,
            dataIndex: 'platforms',
            key: 'platforms',
            width: 200,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: any[]) {
                const len = val.length
                return val.map((item: any, index) => (<span key={item.id}>
                    <span>{ item.productPlatform }</span>
                    {(index < len - 1) && <span>, </span>}
                </span>))
            }
        },
        {
            title: () => <span>{t('buStakeholder')}</span>,
            dataIndex: 'relevanceBuss',
            key: 'relevanceBuss',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: any[]) {
                const len = val.length
                return val.map((item: any, index) => (<span key={item.id}>
                    <span>{ item.bu }</span>
                    {(index < len - 1) && <span>, </span>}
                </span>))
            }
        },
        {
            title: () => <span>{t('developDutySubject')}</span>,
            dataIndex: 'dutyBuss',
            key: 'dutyBuss',
            width: 200,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: any[]) {
                const len = val.length
                return val.map((item: any, index) => (<span key={item.id}>
                    <span>{ item.bu }</span>
                    {(index < len - 1) && <span>, </span>}
                </span>))
            }
        },
        // {
        //     title: () => <span>{t('planBudgeting')}</span>,
        //     dataIndex: 'planBudgeting',
        //     key: 'planBudgeting',
        //     width: 150,
        //     className: 'green_column',
        //     ellipsis: { showTitle: true },
        //     render(val: number) {
        //         if (val === null) {
        //             return ''
        //         }
        //         if (isNaN(Number(val))) {
        //             val = 0
        //         }
        //         val = Math.round(val)
        //         const result = val.toString().replace(/(\d)(?=(?:\d{3})+$)/g, '$1,')
        //         return (<span>{result}</span>)
        //     }
        // },
        {
            title: () => <span>{t('techPlanCost')}</span>,
            dataIndex: 'planBudgeting',
            key: 'planBudgeting',
            width: 250,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: number) {
                if (val === null) {
                    return ''
                }
                if (isNaN(Number(val))) {
                    val = 0
                }
                val = Math.round(val)
                const result = val.toString().replace(/(\d)(?=(?:\d{3})+$)/g, '$1,')
                return (<span>{result}</span>)
            }
        },
        {
            title: () => <span>{t('budgeting')}</span>,
            dataIndex: 'budgeting',
            key: 'budgeting',
            width: 100,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: number) {
                if (val === null) {
                    return ''
                }
                if (isNaN(Number(val))) {
                    val = 0
                }
                val = Math.round(val)
                const result = val.toString().replace(/(\d)(?=(?:\d{3})+$)/g, '$1,')
                return (<span>{result}</span>)
            }
        },
        {
            title: () => <span>{t('toolsEquipment')}</span>,
            dataIndex: 'toolsEquipment',
            key: 'toolsEquipment',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('resourceNeeded')}</span>,
            dataIndex: 'resourceNeeded',
            key: 'resourceNeeded',
            width: 200,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        
        {
            title: () => <span>{t('projectName')}</span>,
            dataIndex: 'projectName',
            key: 'projectName',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(_: any, record: any) {
                return record.project?.projectName
            }
        },
        {
            title: () => <span>{t('approvalDate')}</span>,
            dataIndex: 'approvalDate',
            key: 'approvalDate',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(_: any, record: any) {
                return record.project?.approvalDate
            }
        },
        {
            title: () => <span>{t('estimateClosedDate')}</span>,
            dataIndex: 'estimateClosedDate',
            key: 'estimateClosedDate',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(_: any, record: any) {
                return record.project?.estimateClosedDate
            }
        },
        {
            title: () => <span>{t('actualClosedDate')}</span>,
            dataIndex: 'actualClosedDate',
            key: 'actualClosedDate',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(_: any, record: any) {
                return record.project?.actualClosedDate
            }
        },
        {
            title: () => <span>{t('projectResult')}</span>,
            dataIndex: 'projectResult',
            key: 'projectResult',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(_: any, record: any) {
                return record.project?.projectResult
            }
        },
        {
            title: () => <span>{t('patentCode')}</span>,
            dataIndex: 'patentCode',
            key: 'patentCode',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(_: any, record: any) {
                return record.patent?.patentCode
            }
        },
        {
            title: () => <span>{t('Action')}</span>,
            dataIndex: 'operation',
            key: 'operation',
            width: 150,
            fixed: 'right',
            render: viewButton
        }
    ]
    // 获取 columns
    // 初始根据 columns 获取 dataIndexs
    // filterCond 为 false，取全部，不用过滤
    const getFilterInfo = (filterCond: boolean) => {
        // 获取年
        const _dataIndexs: any[] = []
        const filterFn = (arr: any) => arr.filter((item: any) => {
            if (item.children) {
                item.children = filterFn(item.children)
                return true
            }
            return filterArr.includes(item.dataIndex)
        })
        
        const _arr = defaultColumns.map(col => {
            if (col.dataIndex) {
                _dataIndexs.push(col.dataIndex)
            }
            if (!col.editable) {
                // 判断第一列设置占行数
                if (col.dataIndex === 'classificationName') {
                    return {
                        ...col,
                    }
                } else if (col.children) {
                    _dataIndexs.push(
                        ...col.children.map(chd => chd.dataIndex)
                    )
                    // 多行表头设置子级
                    return {
                        ...col,
                        children: col.children.map(chd => ({
                            ...chd,
                            // filteredValue: filteredInfo[chd.dataIndex] || null,
                            onCell: (record: IProjectInfo) => ({
                                record,
                                editable: chd.editable,
                                dataIndex: chd.dataIndex,
                                title: chd.title,
                            }),
                        }))
                    }
                    // 技术编号列自定义搜索
                } else if (col.dataIndex === 'techPtn') {
                    return {
                        ...col,
                        // ...getColumnSearchProps(col.dataIndex),
                        className: 'search_col',
                        // filteredValue: filteredInfo[col.dataIndex] || null,
                    }
                } else {
                    return {
                        ...col,
                        // filteredValue: (col.dataIndex && filteredInfo[col.dataIndex]) || null,
                    }
                }
            }
            return {
                ...col,
                // filteredValue: (col.dataIndex && filteredInfo[col.dataIndex]) || null,
                onCell: (record: IProjectInfo) => ({
                    record,
                    editable: col.editable,
                    dataIndex: col.dataIndex,
                    title: col.title,
                }),
            }
        })
        // 取出最低年-最高年 增加 年 列数
        let minYear = 9999, maxYear = 0
        projectInfo.forEach(project => {
            project.techYearQuarters.forEach(item => {
                minYear = Math.min(item.year, minYear)
                maxYear = Math.max(item.year, maxYear)
            })
        })
        const { years, yearDataIndexs }: {years: any[], yearDataIndexs: string[]} = getYearColumns(minYear, maxYear)
        _arr.splice(9, 0, ...years)
        _dataIndexs.splice(9, 0, ...yearDataIndexs)
        return {
            columnArr: filterCond ? filterFn(_arr) : _arr,
            dataIndexArr: _dataIndexs
        }
    }

    // 分页
    const onPaginationChange: PaginationProps['onChange'] = (pageNumber: number): void => {
        if (pageSizeChanged.current) return
        getList({
            ...paginationInfo,
            pageIndex: pageNumber
        })
    }
    const onPageSizeChange = (current: number, size: number) => {
        // 避免重复触发 pageChange 事件
        pageSizeChanged.current = true
        getList({
            ...paginationInfo,
            pageIndex: current,
            pageSize: size
        })

        setTimeout(() => {
            pageSizeChanged.current = false
        }, 100)
    }
    useEffect(() => {
        console.log('history.location: ', history.location, locationObj, route, workgroupList)
        setTableKey(getUuid(8))

        if (workgroupList.length === 0) {
            dispatch(getWorkgroup())
        }

        setInit(false)
        
        // 获取字典列表，作为下拉选项
        if (Object.keys(dicClassList).length === 0) {
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
            getDicClass(dispatch, (a: boolean): void => {})
        }

        getList()
        // 设置数据源
        setProjectInfo([])
        
        // 获取 product list
        getProductList()
        // // 获取 BU list
        getBuListFun()

        const offsetH = 290
        const onResize = () => {
            setWinh(document.body.clientHeight - offsetH)
        }
        onResize()
        window.addEventListener('resize', onResize)
        return () => {
            window.removeEventListener('resize', onResize)
        }
    }, [])
    // 从表格数据中剔重得出类型列表
    useEffect(() => {
        const _classification = [...classification]
        const groupByCategory = _classification.reduce((group: any, product) => {
            const { classId } = product
            group[classId] = group[classId] || []
            group[classId].push(product)
            return group
        }, {})
        const _classArr: any[] = []
        Object.keys(groupByCategory).forEach(key => {
            _classArr.push(groupByCategory[key][0])
        })
        setClassArr([..._classArr])
    }, [classification])
    // 用于过滤的字段名
    useEffect(() => {
        if (init) return
        // 获取 columns
        // 初始根据 columns 获取 dataIndexs
        const { columnArr, dataIndexArr }: {columnArr: any[], dataIndexArr: string[]} = getFilterInfo(filterArr.length > 0)
        // 如果一年中所有季度都过滤掉了，则不显示该年
        const columnFilterResult = columnArr.filter(item => {
            return !(typeof item.title === 'string' && item.title.includes('20') && item.children.length === 0)
        })
        setColumns(columnFilterResult)
        setDataIndexs(dataIndexArr)
        if (filterArr.length) {
            localStorage.setItem('filterArrGlobal', filterArr.join(','))
            localStorage.setItem('allLenGlobal', '' + allFilter.current.length)
        }
    }, [filterArr, filteredInfo, projectInfo])
    // 修改数据源时更新 filter column options
    useEffect(() => {
        if (init) return
        // 获取 columns
        // 初始根据 columns 获取 dataIndexs
        let dataIndexArr: string[] = []
        const _filterArr = localStorage.getItem('filterArrGlobal')
        const allLen = localStorage.getItem('allLenGlobal')
        const result = getFilterInfo(false)
        allFilter.current = result.dataIndexArr
        if (!_filterArr && filterArr.length === 0) {
            dataIndexArr = result.dataIndexArr
        } else {
            dataIndexArr = _filterArr?.split(',') as string[]
            setAllCheck(dataIndexArr.length === +(allLen as string))
        }
        setFilterArr(dataIndexArr)
    }, [projectInfo])
    // 过滤显示的字段
    useEffect(() => {
        // 字段列表
        // 获取 options
        const _filterOptions = getFilterOptions()
        setFilterOptions(_filterOptions)
    }, [dataIndexs])
    useEffect(() => {
        if (didMountRef.current) {
            searchFn()
        } else {
            didMountRef.current = true
        }
    }, [searchObj])
    return (
        <DataSourceContext.Provider value={{
            dataSource, 
            setDataSource 
        }}>
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <Nav dispatch={dispatch} />
                <PageBreadcrumb routerParam={routes}
                    history={history}
                    route={route}
                    dispatch={dispatch}
                />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <PageHeader className='chart_title2'
                        onBack={() => null}
                        title=''
                    />
                    <Space>
                        {/* checkbox 多选触发渲染 多选会换行，此处不用原样式 */}
                        <Select defaultValue={['']} value={['']} style={{ width: 180 }}
                            mode="multiple" className='select_col'
                        >
                            <Option value='' className='select_col_filter'>{t('filterColumn')} <DownOutlined /></Option>
                            <Option value='all' className='select_col_option'><Checkbox checked={allCheck}
                                onChange={(e: CheckboxChangeEvent) => {
                                    if (e.target.checked) {
                                        setFilterArr(allFilter.current)
                                    } else {
                                        const _filterArr = allFilter.current.slice(0, 5)
                                        _filterArr.push(allFilter.current[allFilter.current.length - 1])
                                        setFilterArr(_filterArr)
                                    }
                                    setAllCheck(e.target.checked)
                                }}
                            >{t('All')}</Checkbox></Option>
                            { filterOptions }
                        </Select>
                        <Button type='primary'
                            icon={<SearchOutlined />}
                            onClick={() => {
                                setShowSearchPop(!showSearchPop)
                            }}
                        >{t('search')}</Button>
                        {authInfo && Reflect.has(authInfo, 'exportExcel') && <Button type='primary'
                            icon={<CloudDownloadOutlined />}
                            loading={exportLoading}
                            onClick={async () => {
                                setExportLoading(true)
                                const res = await exportExcel(searchObj)
                                    .finally(() => {
                                        setExportLoading(false)
                                    })
                                downloadFile(res, 'All' + '.ProjectInfo.' + res.suffix)
                            }}
                        >{t('export')}</Button>}
                    </Space>
                </div>
                <ConfigProvider locale={i18n.language === 'zh' ? zhCN : undefined}>
                    <ResizableTable bordered
                        key={tableKey}
                        tableData={dataSource}
                        components={components}
                        childrenColumnName='xxx'
                        tableLayout='fixed'
                        cols={columns as ColumnTypes}
                        rowKey="id"
                        scroll={{ x: 'max-content', y: winH }}
                        onChange={handleChange}
                        onRow={handleRow}
                        loading={tableLoading}
                        pagination={{
                            current: paginationInfo.pageIndex,
                            pageSize: paginationInfo.pageSize,
                            total: paginationInfo.total,
                            showQuickJumper: true,
                            onChange: onPaginationChange,
                            showSizeChanger: true,
                            onShowSizeChange: onPageSizeChange,
                            showTotal: (total: number) => 
                                `${t('total')} ${total} ${t('items')}`
                        }}
                    />
                </ConfigProvider>
            </Space>
            <DrawerShowContext.Provider value={{ showSearchPop, setShowSearchPop, setSearchObj, classArr, prodList, buList }}>
                <Table2SearchPop />
            </DrawerShowContext.Provider>
            <LogListPop recordId={viewLogId} tableParam='tech_project_info' setViewLogId={setViewLogId} />
            <HistoryListPop recordId={viewHistoryId} setViewHistoryId={setViewHistoryId} />
        </DataSourceContext.Provider>
    )
}

export default TablePage2
